/* eslint-disable max-len */
import {
  TextField,
  ReferenceField,
  useTranslate,
  FunctionField,
  DateField,
  ExportButton,
  SortButton,
  useResourceContext,
  ReferenceInput,
  SelectInput,
  FilterButton,
  FilterForm,
  useListContext
} from "react-admin";
import dayjs, { Dayjs } from "dayjs";
import * as User from "providers/user";
import CustomList from "./CustomList";
import CustomDataGrid from "./CustomDataGrid";
import UserAvatarAndName from "./UserAvatarAndName";
import { EmptyOrderIcon, FilterIcon, SortIcon } from "assets/Icons";
import AppointmentDatesSelectButton from "./AppointmentDatesSelectButton";
import { useEffect, useState } from "react";
import { DateRange } from "@mui/x-date-pickers-pro";
import {
  Badge,
  ListItemButton,
  Popover,
  Theme,
  useMediaQuery
} from "@mui/material";
import AppointmentDatesSelect from "./AppointmentDatesSelect";
import { Link } from "react-router-dom";
import { CustomButton } from "./CustomButton";
import MobileFilterModal from "./MobileFilterModal";
import { muiColors } from "themes/colors";
import { ICustomSort } from "types";
import { getZonedDate } from "../utils/helpers";
import CustomEmptyList from "./CustomEmptyList";
import AddCardIcon from "@mui/icons-material/AddCard";
import PersonOffIcon from "@mui/icons-material/PersonOff";

const convertString = (str: string) => {
  str = str.toLowerCase();
  str = str.replace(/[\s-]+/g, "_");
  return str;
};

const LocationFilter = ({ source, label }: any) => {
  const { permissions } = User.useUser();
  return (
    <ReferenceInput
      source={source}
      reference="Location"
      label={label}
      filter={{ company_id: permissions.companyId }}
    >
      <SelectInput
        optionText={(value) => value.name}
        format={(value) => (!value ? null : value)}
        label={label}
      />
    </ReferenceInput>
  );
};

const CustomOrderListActions = ({ dateSelector }: { dateSelector: any }) => {
  const translate = useTranslate();
  const { user, setIsNewChargeModalOpen, permissions } = User.useUser();
  const resource = useResourceContext();

  const fields =
    resource === "Appointment-order"
      ? ["created_at", "appointment.start_time"]
      : ["created_at"];

  const customFilters = [
    <LocationFilter
      source="location_id"
      label={translate("appointment.location")}
    />
  ];
  const customMobileFilters = [
    <LocationFilter
      source="location_id"
      label={translate("appointment.location")}
      alwaysOn={true}
    />
  ];
  const baseFields: ICustomSort[] = [
    { field: "created_at", order: "DESC" },
    { field: "created_at", order: "ASC" }
  ];

  const customSortChoices: ICustomSort[] =
    resource === "Appointment-order"
      ? [
          ...baseFields,
          { field: "appointment.start_time", order: "DESC" },
          { field: "appointment.start_time", order: "ASC" }
        ]
      : baseFields;

  const { filterValues, setFilters, setSort, sort } = useListContext();
  const filtersCount =
    (filterValues?.location_id ? 1 : 0) +
    (filterValues?.checked_in_at ? 1 : 0) +
    (filterValues?.cancelled_at ? 1 : 0);
  const isDefaultSort = sort?.field === "created_at" && sort?.order === "DESC";
  const [isLocationMobileFilterModalOpen, setIsLocationMobileFilterModalOpen] =
    useState<boolean>(false);
  const isSmall = useMediaQuery<Theme>("(max-width:1024px)");
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const isPopoverOpen = Boolean(anchorEl);
  const id = isPopoverOpen ? "order-list-sort-actions-popover" : undefined;

  const handleSortButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    setAnchorEl(event.currentTarget);
  };

  function handleIsWebview() {
    if (globalThis.isReactNativeWebview === "true") {
      return true;
    }
    if (globalThis.isReactNativeWebview2 === "yes") {
      return true;
    }
    return false;
  }

  const isWebview = handleIsWebview();

  if (isSmall) {
    return (
      <div
        className={`w-full min-h-[80px] pb-2 ${
          user.impersonate_company_id ? "mt-[30px]" : ""
        } bg-mono-primaryBgColor`}
      >
        <div className="flex justify-between items-center px-4 gap-2 mt-[12px] mb-1">
          <h4 className="font-['OptimaLTStd'] font-[600]">
            {translate(`pagenames.${convertString(resource)}`)}
          </h4>
          <CustomButton
            onClick={() => {
              if (!isDefaultSort) {
                setSort({ field: "created_at", order: "DESC" });
              }
              if (
                !!filterValues?.created_at?.gte &&
                !!filterValues?.created_at?.lte
              ) {
                setFilters(
                  {
                    created_at: {
                      gte: filterValues.created_at.gte,
                      lte: filterValues.created_at.lte
                    }
                  },
                  undefined,
                  false
                );
              } else {
                setFilters({}, undefined, false);
              }
            }}
            variant="tertiary"
            text={translate("common.reset_all")}
            sx={{ color: muiColors.error }}
            disabled={filtersCount === 0 && isDefaultSort}
          />
        </div>
        <div className="w-full h-[50px] overflow-x-hidden relative">
          <div className="absolute w-full h-full bottom-0 left-0 overflow-x-auto hide-scroll-bar">
            <div className="px-4 flex gap-[8px] w-max items-center h-full">
              {resource === "Walk-in-order" &&
                isWebview &&
                permissions?.isCompanyFull && (
                  <CustomButton
                    onClick={() => {
                      setIsNewChargeModalOpen(true);
                    }}
                    startIcon={<AddCardIcon />}
                    variant="secondary"
                    text={`${translate("navigation.new_charge")}`}
                    sx={{
                      backgroundColor: "#FAF9F7",
                      fontSize: "12px",
                      textTransform: "capitalize"
                    }}
                  />
                )}
              {dateSelector}
              <Badge
                color="error"
                badgeContent={isDefaultSort ? 0 : 1}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left"
                }}
                sx={{
                  "& .MuiBadge-badge": {
                    top: "6px",
                    left: "6px"
                  }
                }}
              >
                <div>
                  <CustomButton
                    onClick={handleSortButtonClick}
                    startIcon={<SortIcon />}
                    variant="secondary"
                    text={`${translate("common.sort_by")}: ${translate(
                      `common.${sort.field}`
                    )} - ${sort?.order}`}
                    sx={{
                      backgroundColor: "#FAF9F7",
                      fontSize: "12px",
                      textTransform: "capitalize"
                    }}
                  />
                </div>
              </Badge>
              <Badge
                color="error"
                badgeContent={filtersCount}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left"
                }}
                sx={{
                  "& .MuiBadge-badge": {
                    top: "6px",
                    left: "6px"
                  }
                }}
              >
                <div>
                  <CustomButton
                    onClick={() => setIsLocationMobileFilterModalOpen(true)}
                    startIcon={<FilterIcon />}
                    variant="secondary"
                    text={translate("common.add_filters")}
                    sx={{ backgroundColor: "#FAF9F7", fontSize: "12px" }}
                  />
                </div>
              </Badge>
            </div>
          </div>
        </div>
        {isLocationMobileFilterModalOpen && (
          <MobileFilterModal
            open={isLocationMobileFilterModalOpen}
            onClose={() => setIsLocationMobileFilterModalOpen(false)}
            title={translate("common.add_filters")}
          >
            <div className="flex flex-col items-center">
              <FilterForm filters={customMobileFilters} />
            </div>
          </MobileFilterModal>
        )}
        <Popover
          id={id}
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          elevation={4}
          sx={{
            "& .MuiPopover-paper": {
              backgroundColor: "#fff",
              borderRadius: "4px",
              textTransform: "capitalize"
            }
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
        >
          {customSortChoices
            .filter(
              (curr) => curr.field !== sort?.field || curr.order !== sort.order
            )
            ?.map((item) => (
              <ListItemButton
                onClick={() => {
                  setSort({ field: item.field, order: item.order });
                  setAnchorEl(null);
                }}
                sx={{
                  fontSize: "14px",
                  fontFamily: "Inter",
                  "& .MuiListItemButton-root": {
                    fontSize: "14px",
                    fontFamily: "Inter"
                  }
                }}
              >
                {translate(`common.${item.field}`) + " - " + item.order}
              </ListItemButton>
            ))}
        </Popover>
      </div>
    );
  }

  return (
    <div
      className={`w-full min-h-[80px] ${
        user.impersonate_company_id ? "mt-[30px]" : ""
      } flex justify-between items-center bg-mono-primaryBgColor whitespace-nowrap flex-wrap px-4 xs:px-0`}
    >
      <div className="flex justify-between items-center gap-[16px]">
        <h4 className="font-['OptimaLTStd'] font-[600]">
          {translate(`pagenames.${convertString(resource)}`)}
        </h4>
        <FilterForm filters={customFilters} />
      </div>
      <div className="flex gap-[8px] customListActionsButtonsWrapper">
        {resource === "Walk-in-order" &&
          isWebview &&
          permissions?.isCompanyFull && (
            <CustomButton
              onClick={() => {
                setIsNewChargeModalOpen(true);
              }}
              startIcon={<AddCardIcon />}
              variant="secondary"
              text={`${translate("navigation.new_charge")}`}
              sx={{
                backgroundColor: "#FAF9F7",
                fontSize: "12px",
                textTransform: "capitalize"
              }}
            />
          )}
        <FilterButton filters={customFilters} disableSaveQuery />
        <SortButton fields={fields} label={translate("common.sort_by")} />
        <ExportButton />
        {dateSelector}
      </div>
    </div>
  );
};

export default function OrderList() {
  const { permissions } = User.useUser();
  const translate = useTranslate();
  const resource = useResourceContext();
  const isSmall = useMediaQuery<Theme>("(max-width:800px)");
  // const localStorageString =
  //   resource === "Appointment-order"
  //     ? "RaStore.Appointment-order.listParams"
  //     : "RaStore.Walk-in-order.listParams";
  // const localStorageOrderListParams = localStorage.getItem(localStorageString);
  const localStorageOrderListParams: any = null;

  const parsedLocalStorageOrderListParams =
    localStorageOrderListParams && JSON.parse(localStorageOrderListParams);

  const [dateValue, setDateValue] = useState<DateRange<Dayjs>>([
    parsedLocalStorageOrderListParams?.filter?.created_at?.gte
      ? dayjs(parsedLocalStorageOrderListParams?.filter?.created_at?.gte)
      : dayjs(),
    parsedLocalStorageOrderListParams?.filter?.created_at?.lte
      ? dayjs(parsedLocalStorageOrderListParams?.filter?.created_at?.lte)
      : dayjs().add(7, "day")
  ]);

  const filter =
    resource === "Appointment-order"
      ? {
          appointment: {
            is: { location: { is: { company_id: permissions.companyId } } }
          },
          ...(dateValue?.[0] &&
            dateValue?.[1] && {
              created_at: {
                gte: dateValue?.[0]?.startOf("day").toISOString(),
                lte: dateValue?.[1]?.endOf("day").toISOString()
              }
            })
        }
      : {
          appointment: { is: null },
          location: { is: { company_id: permissions.companyId } },
          ...(dateValue?.[0] &&
            dateValue?.[1] && {
              created_at: {
                gte: dateValue?.[0]?.startOf("day").toISOString(),
                lte: dateValue?.[1]?.endOf("day").toISOString()
              }
            })
        };

  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );

  const handleDatePopoverClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handleDatePopoverClose = () => {
    setDateAnchorEl(null);
  };

  const datePopoverOpen = Boolean(dateAnchorEl);
  const datePopoverId = datePopoverOpen
    ? "simple-order-date-popover"
    : undefined;

  useEffect(() => {
    localStorage.removeItem("RaStore.Appointment-order.listParams");
    localStorage.removeItem("RaStore.Walk-in-order.listParams");
  }, []);

  return (
    <CustomList
      resource={resource}
      filter={filter}
      title={translate(`pagenames.${convertString(resource)}`)}
      hasCreate={false}
      empty={false}
      actions={
        <CustomOrderListActions
          dateSelector={
            <AppointmentDatesSelectButton
              dateValue={dateValue}
              setDateValue={setDateValue}
              handleDatePopoverClick={handleDatePopoverClick}
              fromOrderList
            />
          }
        />
      }
      sort={{ field: "created_at", order: "DESC" }}
    >
      <CustomDataGrid
        bulkActionButtons={false}
        empty={
          <div
            style={{
              height: "calc(100vh - 200px)",
              backgroundColor: "#F6F4F1"
            }}
          >
            <CustomEmptyList
              emptyIcon={<EmptyOrderIcon />}
              emptyTitle={translate("empty_list.no_orders_yet")}
              emptyListCreateSubtitle=""
              createButtonText=""
              hasCreate={false}
            />
          </div>
        }
      >
        <FunctionField
          label={translate("common.user")}
          data-label={translate("common.user") + ":"}
          sortBy="user.first_name.sort"
          render={(record: any) => {
            if (!record?.user) {
              return (
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full flex justify-center items-center bg-[rgba(238,234,228,0.9)]">
                    <PersonOffIcon
                      sx={{
                        width: 20,
                        height: 20
                      }}
                    />
                  </div>
                  <span>{translate("order.walk_in")}</span>
                </div>
              );
            }
            return (
              <UserAvatarAndName
                clickable={false}
                nameStyles="line-clamp-2 break-all text-[14px] min-w-[100px] md:min-w-[70px] xl:min-w-[120px]"
              />
            );
          }}
          sx={{ marginTop: "6px" }}
        />
        {resource === "Appointment-order" && (
          <ReferenceField
            label={translate("order.appointment")}
            source="appointmentId"
            reference="Appointment"
            link={false}
            sortBy="appointment.start_time"
            sortByOrder="ASC"
            emptyText="-"
          >
            <FunctionField
              data-label={translate("order.appointment") + ":"}
              render={(record: any) => {
                return (
                  <div className="flex">
                    <div className="text-[14px] font-[400] font-['Inter']">
                      {dayjs(
                        getZonedDate(
                          record?.start_time,
                          record?.location?.timezone
                        )
                      ).format("hh:mm A")}{" "}
                      -{" "}
                      {dayjs(
                        getZonedDate(
                          record?.end_time,
                          record?.location?.timezone
                        )
                      ).format("hh:mm A")}
                      ,{" "}
                      {dayjs().isSame(
                        dayjs(
                          getZonedDate(
                            record?.start_time,
                            record?.location?.timezone
                          )
                        ).format("MM/DD/YYYY"),
                        "day"
                      )
                        ? "Today"
                        : dayjs(
                            getZonedDate(
                              record?.start_time,
                              record?.location?.timezone
                            )
                          ).format("MM/DD/YYYY")}
                    </div>
                  </div>
                );
              }}
            />
          </ReferenceField>
        )}
        <DateField
          sortBy="created_at"
          sortByOrder="DESC"
          label={translate("order.created_at")}
          data-label={translate("order.created_at") + ":"}
          source="created_at"
        />
        <TextField
          label={translate("order.payment_status")}
          data-label={translate("order.payment_status") + ":"}
          source="payment_status"
          data-cy="order-row"
        />
        <TextField
          emptyText="-"
          label={translate("order.location")}
          data-label={translate("order.location") + ":"}
          source="location.name"
          className="line-clamp-2 break-all text-[14px] min-w-[100px] md:min-w-[70px] xl:min-w-[120px]"
        />
        <FunctionField
          label={translate("order.amount")}
          data-label={translate("order.amount") + ":"}
          sortBy="full_amount.sort"
          render={(record: any) => {
            const amountPaidByUserNotIncludingTips = record?.transactions
              ?.filter(
                (item: any) =>
                  item?.status === "SUCCESS" && item?.type !== "REFUND"
              )
              ?.reduce((acc: number, curr: any) => {
                acc += curr?.amount ?? 0;
                acc -= curr?.payment_fees ?? 0;
                acc -= curr?.total_tip_amount ?? 0;
                return acc;
              }, 0);
            return (
              <div>
                <span className="text-mono-primaryTextColor">
                  ${Number(amountPaidByUserNotIncludingTips.toFixed(2))}
                </span>
                <span className="text-mono-disabledTextColor">
                  /{record.full_amount ?? record.appointment?.full_amount ?? 0}
                </span>
              </div>
            );
          }}
        />
        {isSmall && (
          <FunctionField
            render={(record: any) => (
              <Link
                to={
                  resource === "Appointment-order"
                    ? `/dashboard/Appointment-order/${record?.id}/show`
                    : `/dashboard/Walk-in-order/${record?.id}/show`
                }
                style={{ width: "100%" }}
              >
                <CustomButton
                  variant="secondary"
                  text={translate("common.view_details")}
                  cyId="view-details-button"
                  sx={{ width: "100%", marginBottom: "10px" }}
                />
              </Link>
            )}
          />
        )}
      </CustomDataGrid>
      <Popover
        id={datePopoverId}
        open={datePopoverOpen}
        anchorEl={dateAnchorEl}
        onClose={handleDatePopoverClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: -25,
          horizontal: "center"
        }}
      >
        <AppointmentDatesSelect
          dateValue={dateValue}
          setDateValue={setDateValue}
          handleDatePopoverClose={handleDatePopoverClose}
          fromOrderList
        />
      </Popover>
    </CustomList>
  );
}
